/**
 * O que faz: modal de revisão dos agentes. À esquerda, as sugestões pendentes; ao escolher uma, a conversa do
 *            lead entra deslizando à direita com a decisão (mover/dispensar ou agendar/recusar) ao lado.
 *            Depois de decidir, o próximo item abre sozinho para a revisão seguir em sequência.
 *            Sugestões da organização personalizada (21/10/2026) usam DecisaoOrganizacaoRevisao: critério da coluna,
 *            Aceitar/Manter/Corrigir e "Ajustar regra da coluna".
 * Onde usa: aba Agentes da página /ia (card "Para revisar") e o sino de avisos (/ia?aba=agentes&revisar=1).
 * Conexões: useRevisaoAgentes e useAcoesAgentes (RPCs de decisão), DashboardLeadConversation (mesma conversa
 *           do modal de leads dos anúncios, carregada só quando um item é escolhido).
 * Tabelas: indiretamente lead_return_suggestions, lead_classification_state e chat_mensagens (conversa).
 * Data: 26/09/2026.
 */
import { lazy, Suspense, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarClock, Check, ChevronLeft, ExternalLink, Info, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { useEtapas } from '@/hooks/useEtapasData';
import { TEXTO_MARCO, type ItemRevisao } from '../contratos';
import { chaveItemRevisao, proximoItemRevisao, resumoSugestao } from '../revisao';
import { useAcoesAgentes, useRevisaoAgentes } from '../useAgentes';
import { DecisaoOrganizacaoRevisao } from './DecisaoOrganizacaoRevisao';
import { DecisaoRetornoRevisao } from './DecisaoRetornoRevisao';
import { ListaRevisaoAgentes } from './ListaRevisaoAgentes';

// O histórico e o compositor do chat só entram no bundle quando a pessoa abre uma conversa.
const DashboardLeadConversation = lazy(() => import('@/components/dashboard/DashboardLeadConversation')
  .then((modulo) => ({ default: modulo.DashboardLeadConversation })));

interface ModalRevisaoAgentesProps {
  aberto: boolean;
  onFechar: () => void;
  clinicaId: string;
  modoAdmin: boolean;
  // Item escolhido na prévia da página: o modal já abre com a conversa dele.
  itemInicial?: ItemRevisao | null;
  // "Ajustar regra da coluna": fecha a revisão e abre o assistente de organização como rascunho.
  onAjustarOrganizacao?: () => void;
}

export function ModalRevisaoAgentes({ aberto, onFechar, clinicaId, modoAdmin, itemInicial = null, onAjustarOrganizacao }: ModalRevisaoAgentesProps) {
  const navigate = useNavigate();
  const revisao = useRevisaoAgentes(clinicaId, modoAdmin);
  const acoes = useAcoesAgentes(clinicaId, modoAdmin);
  // Colunas para "Corrigir sugestão": mesma consulta em cache do Pipeline, só com o modal aberto.
  const { data: etapasClinica = [] } = useEtapas(clinicaId, { enabled: aberto });
  const etapasCorrecao = etapasClinica.map((etapa) => ({ id: etapa.id, nome: etapa.nome }));
  const itens = revisao.data ?? [];
  const [selecionado, setSelecionado] = useState<ItemRevisao | null>(itemInicial);
  // A ficha do lead começa recolhida, como no modal de leads dos anúncios.
  const [fichaAberta, setFichaAberta] = useState(false);

  // Cada abertura começa do item pedido (ou só da lista); fechar limpa a seleção.
  useEffect(() => {
    if (aberto) setSelecionado(itemInicial);
    else { setSelecionado(null); setFichaAberta(false); }
  }, [aberto, itemInicial]);

  const ocupado = acoes.aprovarRetorno.isPending || acoes.recusarRetorno.isPending
    || acoes.aceitarEtapa.isPending || acoes.dispensarEtapa.isPending || Boolean(acoes.corrigirOrganizacao?.isPending);

  // Correção explícita: move o card como pessoa e pausa só este contato; depois segue para o próximo item.
  const corrigir = async (item: ItemRevisao, etapaId: string) => {
    const proximo = proximoItemRevisao(itens, item);
    await acoes.corrigirOrganizacao.mutateAsync({ leadId: item.leadId, etapaId, etapaAlteradaEm: item.etapaAlteradaEm });
    setSelecionado(proximo);
  };

  // Depois de cada decisão, segue para o próximo item da lista atual.
  // agendarPara: data escolhida na revisão; sem ela, o banco usa a data sugerida pela IA.
  const decidir = (item: ItemRevisao, decisao: 'aceitar' | 'recusar', agendarPara: string | null = null) => {
    const proximo = proximoItemRevisao(itens, item);
    const opcoes = { onSuccess: () => setSelecionado(proximo) };
    if (item.tipo === 'retorno' && item.itemId) {
      if (decisao === 'aceitar') acoes.aprovarRetorno.mutate({ sugestaoId: item.itemId, agendarPara }, opcoes);
      else acoes.recusarRetorno.mutate(item.itemId, opcoes);
      return;
    }
    if (decisao === 'aceitar') acoes.aceitarEtapa.mutate(item.leadId, opcoes);
    else acoes.dispensarEtapa.mutate(item.leadId, opcoes);
  };

  const abrirNoChat = () => {
    if (!selecionado) return;
    const parametros = new URLSearchParams({ leadId: selecionado.leadId });
    if (modoAdmin) parametros.set('adminClinicId', clinicaId);
    onFechar();
    navigate(`/chat?${parametros.toString()}`);
  };

  const total = itens.length;

  return (
    <Dialog open={aberto} onOpenChange={(valor) => { if (!valor) onFechar(); }}>
      <DialogContent className="flex h-[94dvh] w-[calc(100vw-1rem)] max-w-6xl flex-col gap-0 overflow-hidden rounded-[28px] p-0 sm:w-[94vw]">
        <DialogHeader className="shrink-0 border-b border-border px-4 py-3 pr-12 text-left sm:px-5 sm:pr-12">
          <DialogTitle className="text-base">Revisão dos agentes</DialogTitle>
          <DialogDescription className="text-xs">
            {revisao.isLoading ? 'Carregando sugestões…' : `${total.toLocaleString('pt-BR')} ${total === 1 ? 'sugestão esperando' : 'sugestões esperando'} decisão.`}
            {' '}Escolha uma para ler a conversa e decidir.
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1">
          {/* Lista: some no celular quando há conversa aberta; em telas largas vira coluna fixa. */}
          <section
            aria-label="Sugestões para revisar"
            aria-busy={revisao.isLoading}
            className={cn('min-w-0 flex-1 flex-col animate-in fade-in-0 duration-200 motion-reduce:animate-none', !selecionado ? 'flex' : fichaAberta ? 'hidden border-r border-border xl:flex xl:max-w-[18rem]' : 'hidden border-r border-border md:flex md:max-w-sm')}
          >
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
              {revisao.isLoading ? (
                <p role="status" className="flex items-center justify-center gap-2 p-8 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />Carregando sugestões…</p>
              ) : revisao.isError ? (
                <div className="flex flex-col items-center gap-3 p-8 text-center">
                  <p role="alert" className="text-sm text-muted-foreground">Não foi possível carregar as sugestões.</p>
                  <Button variant="outline" onClick={() => void revisao.refetch()}>Tentar novamente</Button>
                </div>
              ) : total === 0 ? (
                <div className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center animate-in fade-in-0 zoom-in-95 duration-300 motion-reduce:animate-none">
                  <span className="lg-pontos flex h-11 w-11 items-center justify-center rounded-2xl"><Check className="h-5 w-5 text-accent-foreground" aria-hidden="true" /></span>
                  <p className="font-semibold text-foreground">Tudo revisado</p>
                  <p className="max-w-xs text-sm text-muted-foreground">Quando os agentes sugerirem uma etapa ou um retorno, aparece aqui.</p>
                </div>
              ) : (
                <ListaRevisaoAgentes
                  itens={itens}
                  selecionadoChave={selecionado ? chaveItemRevisao(selecionado) : null}
                  onSelecionar={setSelecionado}
                  desabilitado={ocupado}
                />
              )}
            </div>
          </section>

          {selecionado && (
            <section
              key={chaveItemRevisao(selecionado)}
              aria-label={`Conversa de ${selecionado.leadNome ?? 'lead sem nome'}`}
              // A conversa entra deslizando da direita (só transform e opacidade); sem movimento se reduzido.
              className="flex min-w-0 flex-1 flex-col bg-background animate-in fade-in slide-in-from-right-8 duration-200 motion-reduce:animate-none"
            >
              <header className="flex shrink-0 items-center gap-2 border-b border-border px-3 py-2">
                <Button variant="ghost" className="min-h-11 min-w-11 p-2" aria-label="Voltar à lista de sugestões" onClick={() => setSelecionado(null)}>
                  <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                </Button>
                <p className="min-w-0 flex-1 truncate text-sm font-semibold">{selecionado.leadNome ?? 'Lead sem nome'}</p>
                {!fichaAberta && (
                  <Button variant="ghost" className="min-h-11 min-w-11 gap-2" aria-label="Mostrar informações do lead" aria-expanded={fichaAberta} onClick={() => setFichaAberta(true)}>
                    <Info className="h-4 w-4" aria-hidden="true" /><span className="hidden lg:inline">Mostrar lead</span>
                  </Button>
                )}
                <Button variant="outline" className="min-h-11 min-w-11 gap-2 rounded-full" aria-label="Abrir conversa na aba de chat" onClick={abrirNoChat}>
                  <ExternalLink className="h-4 w-4" aria-hidden="true" /><span className="hidden lg:inline">Abrir no chat</span>
                </Button>
              </header>

              {/* Barra de decisão: o que a IA sugeriu, por quê, e as duas respostas possíveis. */}
              <div className="shrink-0 space-y-2 border-b border-border bg-muted px-4 py-3">
                <div className="flex flex-wrap items-center gap-2">
                  {selecionado.tipo === 'retorno'
                    ? <span className="lg-selo lg-selo--info gap-1"><CalendarClock className="h-3 w-3" aria-hidden="true" />Retorno sugerido</span>
                    : selecionado.personalizada
                      ? <span className="lg-selo lg-selo--neutro">Organização das colunas</span>
                      : <span className="lg-selo lg-selo--neutro">{selecionado.marco ? TEXTO_MARCO[selecionado.marco].rotulo : 'Etapa sugerida'}</span>}
                  <span className="text-xs text-muted-foreground">{resumoSugestao(selecionado)}</span>
                </div>
                {selecionado.motivo && <p className="text-sm text-foreground">{selecionado.motivo}</p>}
                {selecionado.tipo === 'retorno' ? (
                  // Retorno: a data pode ser ajustada antes de aprovar (a IA estima quando o lead não diz).
                  <DecisaoRetornoRevisao
                    key={chaveItemRevisao(selecionado)}
                    item={selecionado}
                    ocupado={ocupado}
                    onAgendar={(agendarPara) => decidir(selecionado, 'aceitar', agendarPara)}
                    onRecusar={() => decidir(selecionado, 'recusar')}
                  />
                ) : selecionado.personalizada ? (
                  // Sugestão por regra de coluna: critério confirmado, Aceitar/Manter/Corrigir e ajuste da regra.
                  <DecisaoOrganizacaoRevisao
                    key={chaveItemRevisao(selecionado)}
                    item={selecionado}
                    etapas={etapasCorrecao}
                    etapaAtualId={etapasCorrecao.find((etapa) => etapa.nome === selecionado.etapaAtual)?.id ?? null}
                    ocupado={ocupado}
                    onAceitar={() => decidir(selecionado, 'aceitar')}
                    onManter={() => decidir(selecionado, 'recusar')}
                    onCorrigir={(etapaId) => corrigir(selecionado, etapaId)}
                    onAjustarRegra={onAjustarOrganizacao ? () => { onFechar(); onAjustarOrganizacao(); } : undefined}
                  />
                ) : (
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" className="rounded-full" disabled={ocupado} onClick={() => decidir(selecionado, 'aceitar')}>
                    {ocupado ? <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden="true" /> : <Check className="mr-1 h-4 w-4" aria-hidden="true" />}
                    Mover card
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full" disabled={ocupado} onClick={() => decidir(selecionado, 'recusar')}>
                    <X className="mr-1 h-4 w-4" aria-hidden="true" />
                    Dispensar
                  </Button>
                </div>
                )}
              </div>

              <div className="min-h-0 flex-1">
                <Suspense fallback={<p role="status" className="p-5 text-sm text-muted-foreground">Abrindo conversa…</p>}>
                  {/* A conversa busca o lead completo pelo id; aqui só vão os campos que a revisão conhece. */}
                  <DashboardLeadConversation
                    key={selecionado.leadId}
                    lead={{ id: selecionado.leadId, nome: selecionado.leadNome ?? '', telefone: '', clinica_id: clinicaId, ad_name: null, created_at: selecionado.criadoEm, origem_lead: null }}
                    clinicaId={clinicaId}
                    isAdminMode={modoAdmin}
                    mostrarInfo={fichaAberta}
                    onFecharInfo={() => setFichaAberta(false)}
                  />
                </Suspense>
              </div>
            </section>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
